import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Button, Modal, Steps, AutoComplete, message as MessageDialog } from 'antd';
import useForm from '../../shared/useFormHook';
import Input from '../../shared/input/Input';
import { debounce, get } from 'lodash';
import { getSkillCertList } from '../../../../services/Dashboard';
import TuringHelpBtn from '../../shared/TuringHelpBtn/TuringHelpBtn';
import StatusBadge from '../../shared/StatusBadge/StatusBadge';
import Validator from 'email-validator';
import pick from 'lodash/pick';
import useFlagsFromBackend, { GB_FLAGS } from '../../../services/launchDarkly';
import CERTIFICATION_STATUS from '../../../constants/cloudCertificationStatus';

const { Step } = Steps;
function AdditionalInformationForm ({ service, customClass }) {
  const { onPanelSelect, onSaveClick, removeEvent, save, setCompleted, addSugg } = service;
  const { Publications, Certifications, CloudCertifications = [] } = service.state;
  const { isReadOnly } = service.props;
  const [addNew, setAddNew] = useState(false);
  const [addNewCertifications, setAddNewCertifications] = useState(false);
  const [addNewCloudCertification, setAddNewCloudCertification] = useState(false);
  const [disableSaveButton, setDisableSaveButton] = useState(false);
  const [newItem, setNewItem] = useState(null);
  const [newItemCertifications, setNewItemCertifications] = useState(null);
  const [newItemCloudCertifications, setNewItemCloudCertifications] = useState(null);
  const { $flagsReady, gb_cloud_cert_enable_on_resume_builder } = useFlagsFromBackend([GB_FLAGS.gb_cloud_cert_enable_on_resume_builder]);

  const onSave = useCallback(async (isAutoSave) => {
    let listItemsToSave = [...Publications];
    if (newItem && addNew && !isAutoSave) listItemsToSave.push(newItem);
    listItemsToSave = listItemsToSave.map(item => item.save(isAutoSave));
    let successResult = true;
    const success = (await Promise.all(listItemsToSave)).every(success => {
      if (success === 'Publications') successResult = 'Publications';
      return success;
    });
    if (!isAutoSave) {
      if (success) {
        setAddNew(false);
        setNewItem(null);
      } else {
        onPanelSelect('AdditionalInformation');
        setCompleted('AdditionalInformation', false);
      }
    }
    return success ? successResult : false;
  }, [newItem, addNew, Publications]);

  const onSaveCertifications = useCallback(async (isAutoSave) => {
    let listItemsToSave = [...Certifications];
    if (newItemCertifications && addNewCertifications && !isAutoSave) listItemsToSave.push(newItemCertifications);
    listItemsToSave = listItemsToSave.map(item => item.save(isAutoSave));
    let successResult = true;
    const success = (await Promise.all(listItemsToSave)).every(success => {
      if (success === 'Certifications') successResult = 'Certifications';
      return success;
    });
    if (!isAutoSave) {
      if (success) {
        setAddNewCertifications(false);
        setNewItemCertifications(null);
      } else {
        onPanelSelect('AdditionalInformation');
      }
    }
    return success ? successResult : false;
  }, [newItemCertifications, addNewCertifications, Certifications]);

  const onSaveCloudCertifications = useCallback(async (isAutoSave) => {
    let listItemsToSave = [...CloudCertifications];
    if (newItemCloudCertifications && addNewCloudCertification && !isAutoSave) listItemsToSave.push(newItemCloudCertifications);
    listItemsToSave = listItemsToSave.map(item => item.save(isAutoSave));
    let successResult = true;
    const success = (await Promise.all(listItemsToSave)).every(success => {
      if (success === 'CloudCertifications') successResult = 'CloudCertifications';
      return success;
    });
    if (!isAutoSave) {
      if (success) {
        setAddNewCloudCertification(false);
        setNewItemCloudCertifications(null);
      } else {
        onPanelSelect('AdditionalInformation');
      }
    }
    return success ? successResult : false;
  }, [newItemCloudCertifications, addNewCloudCertification, CloudCertifications]);

  useEffect(() => {
    onSaveClick('Publications', onSave);
    addSugg('AdditionalInformation', { id: 'AdditionalInformation1', content: <p>And don&apos;t forget to highlight your awards.😃</p> });
    return () => removeEvent('Publications');
  }, [onSave]);

  useEffect(() => {
    onSaveClick('Certifications', onSaveCertifications);
    return () => removeEvent('Certifications');
  }, [onSaveCertifications]);

  useEffect(() => {
    onSaveClick('CloudCertifications', onSaveCloudCertifications);
    return () => removeEvent('CloudCertifications');
  }, [onSaveCloudCertifications]);

  const addNewItem = () => {
    if (!newItem) {
      const item = {};
      setNewItem(item);
    }
    setAddNew(true);
  };

  const addNewItemCloudCertification = () => {
    if (!newItemCloudCertifications) {
      const item = {};
      setNewItemCloudCertifications(item);
    }
    setAddNewCloudCertification(true);
  };

  const addNewItemCertifications = () => {
    if (!newItemCertifications) {
      const item = {};
      setNewItemCertifications(item);
    }
    setAddNewCertifications(true);
  };

  const handleSave = () => {
    setDisableSaveButton(true);
    Promise.all([save('Publications'), save('Certifications')])
      .finally(() => setDisableSaveButton(false));
  };

  const handleSubmitCloudCertifications = () => {
    setDisableSaveButton(true);
    Promise.all([save('CloudCertifications', false, false)])
      .finally(() => setDisableSaveButton(false));
  };

  const handleCancelAddingNewPublicationItem = () => {
    setNewItem(null);
    setAddNew(false);
  };

  const handleCancelAddingNewCloudCertificationItem = () => {
    setAddNewCloudCertification(false);
    setNewItemCloudCertifications(null);
  };

  const handleCancelAddingNewCertificationItem = () => {
    setNewItemCertifications(null);
    setAddNewCertifications(false);
  };

  return (
    <div className={customClass || ''}>
      <div className="TuringStepsFormv2">
        <div className="TuringStepsFormv2-title AdditionalInformationForm-section-title"><span className="material-icons">menu_book</span> Publications</div>
        <p>Publications can position you as a subject matter expert in your field. List all the publications and research that are listed.</p>
        <Steps progressDot current={Publications.length - 1} direction="vertical">
          {Publications.map(item => <PublicationsItem key={item.id} service={service} item={item} />)}
          {addNew ? (
            <PublicationsItem service={service} item={newItem} cancelAddingNewItem={handleCancelAddingNewPublicationItem} />
          ) : (isReadOnly ? null : (
            <Step description={<span className="additembtn" onClick={addNewItem}><span className="material-icons" aria-hidden="true">add</span> Add publications</span>} />
          ))}
        </Steps>
      </div>
      {
        $flagsReady && gb_cloud_cert_enable_on_resume_builder &&
     <><div className="additional_info_seperator"></div>
       <div className="TuringStepsFormv2">
         <div className="TuringStepsFormv2-title AdditionalInformationForm-section-title">
           <span className="material-icons">cloud_queue</span> Cloud Certifications</div>
         <Steps progressDot current={CloudCertifications.length - 1} direction="vertical">
           {CloudCertifications.map(item => <CloudCertificationItem key={item.id} service={service} item={item} handleSubmit={handleSubmitCloudCertifications} />)}
           {addNewCloudCertification ? (
             <CloudCertificationItem service={service} item={newItemCloudCertifications} cancelAddingNewItem={handleCancelAddingNewCloudCertificationItem} handleSubmit={handleSubmitCloudCertifications} />
           ) : (isReadOnly ? null : (
             <Step description={<span className="additembtn" onClick={addNewItemCloudCertification}><span className="material-icons" aria-hidden="true">add</span> Add Certificate</span>} />
           ))}
         </Steps>
       </div>
       <div className="additional_info_seperator"></div></>
      }
      <div className="TuringStepsFormv2">
        <div className="TuringStepsFormv2-title AdditionalInformationForm-section-title"><span className="material-icons">emoji_events</span> Certifications & Awards</div>
        <p>Certifications show your interest in the subject and prove that you have a specific expertise, which is issued by an authorized organization. Show the U.S. recruiters your unique traits and qualifications.</p>
        <Steps progressDot current={Certifications.length - 1} direction="vertical">
          {Certifications.map(item => <CertificationsItem key={item.id} service={service} item={item} />)}
          {addNewCertifications ? (
            <CertificationsItem service={service} item={newItemCertifications} cancelAddingNewItem={handleCancelAddingNewCertificationItem} />
          ) : (isReadOnly ? null : (
            <Step description={<span className="additembtn" onClick={addNewItemCertifications}><span className="material-icons" aria-hidden="true">add</span> Add certifications</span>} />
          ))}
        </Steps>
      </div>
      {isReadOnly ? null : (
        <div className="FinalizeResumeV2FormBtns">
          <Button onClick={() => { setAddNew(false); onPanelSelect(null); }}>Cancel</Button>
          <Button disabled={disableSaveButton} type="primary" onClick={handleSave}>Update information</Button>
        </div>
      )}
    </div>
  );
}

AdditionalInformationForm.propTypes = {
  service: PropTypes.any,
  customClass: PropTypes.string
};

const years = [];
for (let i = 0; i <= 35; i++) {
  years.push({
    value: new Date().getFullYear() - i,
    label: new Date().getFullYear() - i
  });
}

const PublicationsItem = ({ item, service, ...props }) => {
  const [open, setOpen] = useState(false);
  const { loadData } = service;
  const { isReadOnly } = service.props;
  const { bind, setData, getData, setError, getErrors, resetError, getChanges } = useForm({}, service.saveDebounce);

  useEffect(() => {
    if (!item.id) {
      setOpen(true);
    }
  }, [item.id]);

  useEffect(() => {
    if (!item) return;
    setData(item);
    resetError();

    item.save = async (isAutoSave) => {
      if (!isAutoSave) resetError();
      const fields = ['title', 'link', 'year'];
      const changedFields = await getChanges(item, fields);
      if (!changedFields.length) return true;
      const data = await getData();
      if (isAutoSave) {
        if (!data.id || fields.find(field => !data[field])) {
          return false;
        }
      } else {
        fields.forEach(field => {
          if (!data[field]) {
            setError(field);
          }
        });
      }
      const errors = await getErrors();
      if (errors.length) {
        if (isAutoSave) return false;
        setOpen(true);
      } else {
        const promiseObj = data.id ? service.updatePublication(data) : service.addPublication(data);
        return promiseObj
          .then(() => {
            if (isAutoSave) {
              service.setState((s) => {
                return { ...s, Publications: s.Publications.map(elm => elm.id === data.id ? { ...elm, ...data } : elm) };
              });
            }
            return 'Publications';
          })
          .catch(error => {
            const { message, stack, code } = error;
            console.error(message, stack, code);
          });
      }
      return errors.length === 0;
    };

    item.delete = (e) => {
      if (e && e.stopPropagation) e.stopPropagation();
      Modal.confirm({
        className: 'delete-item-modal',
        title: <h1 className="Delete-work-experience">Delete the publication?</h1>,
        content: 'This action cannot be undone.',
        okText: 'Yes, delete',
        okType: 'danger',
        cancelText: 'Cancel',
        iconType: null,
        width: 400,
        onOk: () => {
          if (!item.id) {
            if (props.cancelAddingNewItem) props.cancelAddingNewItem();
            return;
          }
          return service.deletePublication(item)
            .then(result => {
              loadData(['Publications']);
            })
            .catch(error => {
              const { message, stack, code } = error;
              console.error(message, stack, code);
            });
        }
      });
    };
  }, [item]);

  let title = (
    <div className="PublicationsItem-title" onClick={() => setOpen(!open)}>
      <span className="PublicationsItem-degree">{item.title}</span>
      {open && !isReadOnly ? <span onClick={item.delete} style={{ color: '#F86567' }} className="material-icons" aria-hidden="true">highlight_off</span> : null}
      <span className="material-icons" aria-hidden="true">{open ? 'expand_less' : 'expand_more'}</span>
    </div>
  );

  if (!item.id) {
    title = (
      <div className="PublicationsItem-title">
        Add publications
        <span style={{ flex: 1 }} />
        <span onClick={item.delete} style={{ color: '#F86567' }} className="material-icons" aria-hidden="true">highlight_off</span>
      </div>
    );
  }

  let form = null;
  if (open) {
    form = (
      <div className="PublicationsItem-form">
        <div className="a"><Input label="Publication title" {...bind('title')} /></div>
        <div className="b"><Input label="URL" {...bind('link')} /></div>
        <div className="c"><Input.Select options={years} label="Year" {...bind('year')} /></div>
      </div>
    );
  }

  return (
    <Step title={title} description={form} {...props} />
  );
};

PublicationsItem.propTypes = {
  item: PropTypes.any,
  cancelAddingNewItem: PropTypes.any,
  service: PropTypes.any
};

const CloudCertificationItem = ({ item, service, handleSubmit, ...props }) => {
  const [open, setOpen] = useState(false);
  const [referenceRequired, setReferenceRequired] = useState(false);
  const [certificateType, setCertificateType] = useState(item.certification_type_id);
  const { loadData, getCloudCertificationTypes } = service;
  const { isReadOnly } = service.props;
  const certificationList = getCloudCertificationTypes();
  const [isSaved, setIsSaved] = useState();
  const { bind, bindFile, setData, getData, setError, getErrors, resetError, getChanges, setValue } = useForm({}, service.saveDebounce);

  useEffect(() => {
    if (!item.id) {
      setOpen(true);
    }
  }, [item.id]);

  useEffect(() => {
    const certificationType = certificationList.find(x => x.value === certificateType);
    const certificateReferenceRequired = certificationType ? certificationType.referenceRequired : false;
    setReferenceRequired(certificateReferenceRequired);
  }, [certificateType]);

  useEffect(() => {
    if (!item) return;
    setData(item);
    resetError();

    item.save = async (isAutoSave) => {
      if (!isAutoSave) resetError();
      const fields = ['certification_type_id', 'sd_issue_year', 'certificate_id', 'certification_link', 'certification_email_id', 'certificate_file'];
      const requiredFields = ['certification_type_id', 'sd_issue_year', 'certification_email_id'];
      const changedFields = await getChanges(item, fields);

      if (!changedFields.length || isAutoSave) return true;

      const data = await getData();
      requiredFields.forEach(field => {
        if (!data[field]) {
          setError(field);
        }
      });

      const certificationType = certificationList.find(x => x.value === data.certification_type_id);
      const certificateReferenceRequired = certificationType ? certificationType.referenceRequired : false;

      if (certificateReferenceRequired) {
        if (!data.certificate_id && !data.certification_link) {
          setError('certificate_id');
          setError('certification_link');
        }
      }

      if (data.certification_email_id) {
        if (!Validator.validate(data.certification_email_id)) {
          setError('certification_email_id');
        }
      }
      if (data.sd_issue_year) {
        const maxDate = new Date().getFullYear();
        if (isNaN(parseInt(data.sd_issue_year))) {
          setError('sd_issue_year');
        } else {
          if (data.sd_issue_year < 1987 || data.sd_issue_year > maxDate) {
            setError('sd_issue_year');
          }
        }
      }
      const errors = await getErrors();

      if (errors.length) {
        if (isAutoSave) return false;
        setOpen(true);
      } else {
        let saveData = null;
        if (data.id) {
          saveData = pick(data, fields.concat('id'));
          if (!(saveData.certificate_file instanceof File)) {
            if (saveData.certificate_file) {
              saveData.keep_existing_file = true;
            } else {
              saveData.keep_existing_file = false;
            }
            delete saveData.certificate_file;
          }
        } else {
          saveData = pick(data, fields);
        }
        const promiseObj = data.id ? service.updateCloudCertification(saveData) : service.addCloudCertification(saveData);
        return promiseObj
          .then(({ data }) => {
            if (data.success) {
              setIsSaved(true);
              setTimeout(() => {
                setIsSaved(false);
              }, 2000);
              return 'CloudCertifications';
            } else {
              console.error(data.message);
              MessageDialog.error(data.message, 3);
            }
          })
          .catch(error => {
            const { message, stack, code } = error;
            console.error(message, stack, code);
            MessageDialog.error('Something went wrong, Please try again later.', 1);
          });
      }
      return errors.length === 0;
    };

    item.delete = (e) => {
      if (e && e.stopPropagation) e.stopPropagation();
      Modal.confirm({
        className: 'delete-item-modal',
        title: <h1 className="Delete-work-experience">Delete the certification?</h1>,
        content: 'This action cannot be undone.',
        okText: 'Yes, delete',
        okType: 'danger',
        cancelText: 'Cancel',
        iconType: null,
        width: 400,
        onOk: () => {
          if (!item.id) {
            if (props.cancelAddingNewItem) props.cancelAddingNewItem();
            return;
          }
          return service.deleteCloudCertification(item)
            .then(result => {
              loadData(['CloudCertifications']);
            })
            .catch(error => {
              const { message, stack, code } = error;
              console.error(message, stack, code);
            });
        }
      });
    };
  }, [item]);

  const onFileSelect = (file) => {
    setValue('certificate_file', file);
  };

  let title = null;
  if (item.id) {
    const titleObject = certificationList.find(x => x.value === item.certification_type_id);
    title = (
      <div className="PublicationsItem-title" onClick={() => setOpen(!open)}>
        <span className="PublicationsItem-degree">{titleObject.label} </span>
        <StatusBadge type={item.verification_status} />
        {open && !isReadOnly ? <span onClick={item.delete} style={{ color: '#F86567' }} className="material-icons" aria-hidden="true">highlight_off</span> : null}
        <span className="material-icons" aria-hidden="true">{open ? 'expand_less' : 'expand_more'}</span>
      </div>
    );
  }
  let filenamelabel;
  if (item.certificate_file) {
    const filename_timeStamp = item.certificate_file;
    filenamelabel = filename_timeStamp.substring(filename_timeStamp.indexOf('_') + 1);
  }

  if (!item.id) {
    title = (
      <div className="PublicationsItem-title">
        Add certificate
        <span style={{ flex: 1 }} />
        <span onClick={item.delete} style={{ color: '#F86567' }} className="material-icons" aria-hidden="true">highlight_off</span>
      </div>
    );
  }

  const certificationTypeBindProps = { ...bind('certification_type_id') };
  let form = null;
  if (open) {
    form = (
      <div className="CloudCertitificationItem-form">
        {
          item.verification_status === CERTIFICATION_STATUS.NEEDS_INFO &&
          <p className="CloudCertitificationItem-needsInfoText">
            This certificate will be verified by our team, please provide the following information to go ahead with the validation.
          </p>
        }
        <div className="CloudCertitificationItem-form">
          <div className="CloudCertitificationItem-form-certificate-name">
            <Input.Select options={certificationList} label={<>Certificate Name <span className='required'>*</span></>} colon={false} {...certificationTypeBindProps} onChange={(e) => { setCertificateType(e); certificationTypeBindProps.onChange(e); }} />
          </div>
          <div className="CloudCertitificationItem-form-certificate-year">
            <Input.Select options={years} label={<>Year of Issue <span className='required'>*</span></>} colon={false} {...bind('sd_issue_year')} />
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
          <div style={{ lineHeight: '40px' }}>
            <TuringHelpBtn help="For AWS/Azure please share the Credly profile link, for GCP share the certificate URL credentials.net & for Salesforce share the certificate email ID.">
              Certificate ID / Validation ID
              or
              Certification Link {referenceRequired ? <span className='required'>*</span> : null}
            </TuringHelpBtn>
          </div>
          <div className="CloudCertitificationItem-form" style={{ justifyContent: 'space-between' }}>
            <div className="CloudCertitificationItem-form-child">
              <Input
                placeholder="Certificate ID / Validation ID"
                colon={false} {...bind('certificate_id')} />
            </div>
            <div style={{ margin: '0 10px 24px 10px', display: 'flex', alignItems: 'center' }}>
              or
            </div>
            <div className="CloudCertitificationItem-form-child">
              <Input placeholder="Certification Link" colon={false} {...bind('certification_link')} />
            </div>
          </div>
        </div>

        <div className="CloudCertitificationItem-form">
          <div style={{ width: '100%' }}>
            <Input placeholder="Certification Email ID" className="CloudCertitificationItem-form-child-link" label={<><TuringHelpBtn help="This should be the email ID used to appear for the certification exam.">Certification Email ID <span className='required'>*</span> </TuringHelpBtn></>} colon={false} {...bind('certification_email_id')} />
          </div>
        </div>

        <div className="CloudCertitificationItem-form">
          <div className="CloudCertitificationItem-file">
            <Input.Upload style={{ width: '100%' }} accept=".doc,.docx,.pdf,.jpg,.jpeg,.png" filename={filenamelabel} filenamePlaceholder='Certificate upload' onFileSelect={onFileSelect} label="Certificate Upload" colon={false} {...bindFile('certificate_file')} />
          </div>
          <div className="submitbtn_wrapper" style={{ width: '128px', alignSelf: 'center' }}>
            <Button onClick={handleSubmit} className="submit_btn" type="primary">Submit</Button>
          </div>
        </div>

        {
          isSaved &&
          <div className="CloudCertitificationItem-form">
            <div style={{ marginLeft: 'auto' }}>
              <div className='cloud-success-message'>
                Certificate saved and submited for review
              </div>
            </div>
          </div>
        }

      </div>
    );
  }

  return (
    <Step title={title} description={form} {...props} />
  );
};

CloudCertificationItem.propTypes = {
  item: PropTypes.any,
  cancelAddingNewItem: PropTypes.any,
  service: PropTypes.any,
  handleSubmit: PropTypes.func
};

const CertificationsItem = ({ item, service, ...props }) => {
  const [open, setOpen] = useState(false);
  const [searching, setSearching] = useState(false);
  const [baseCertList, setCertList] = useState([]);
  const { loadData } = service;
  const { isReadOnly } = service.props;
  const { bind, data, setData, setValue, getData, setError, getErrors, resetError, getChanges } = useForm({}, service.saveDebounce);

  useEffect(() => {
    if (!item.id) {
      setOpen(true);
    }
  }, [item.id]);

  useEffect(() => {
    if (!item) return;
    setData(item);
    resetError();

    item.save = async (isAutoSave) => {
      if (!isAutoSave) resetError();
      const fields = ['name', 'year'];
      const changedFields = await getChanges(item, fields);
      if (!changedFields.length) return true;
      const data = await getData();
      if (isAutoSave) {
        if (!data.id || fields.find(field => !data[field])) {
          return false;
        }
      } else {
        fields.forEach(field => {
          if (!data[field]) {
            setError(field);
          }
        });
      }
      const errors = await getErrors();
      if (errors.length) {
        if (isAutoSave) return false;
        setOpen(true);
      } else {
        const promiseObj = data.id ? service.updateCertification(data) : service.addCertification(data);
        return promiseObj
          .then(() => {
            if (isAutoSave) {
              service.setState((s) => {
                return { ...s, Certifications: s.Certifications.map(elm => elm.id === data.id ? { ...elm, ...data } : elm) };
              });
            }
            return 'Certifications';
          })
          .catch(error => {
            const { message, stack, code } = error;
            console.error(message, stack, code);
          });
      }
      return errors.length === 0;
    };

    item.delete = (e) => {
      if (e && e.stopPropagation) e.stopPropagation();
      Modal.confirm({
        className: 'delete-item-modal',
        title: <h1 className="Delete-work-experience">Delete the certification?</h1>,
        content: 'This action cannot be undone.',
        okText: 'Yes, delete',
        okType: 'danger',
        cancelText: 'Cancel',
        iconType: null,
        width: 400,
        onOk: () => {
          if (!item.id) {
            if (props.cancelAddingNewItem) props.cancelAddingNewItem();
            return;
          }
          return service.deleteCertification(item)
            .then(result => {
              loadData(['Certifications']);
            })
            .catch(error => {
              const { message, stack, code } = error;
              console.error(message, stack, code);
            });
        }
      });
    };
  }, [item]);

  let title = (
    <div className="CertificationsItem-title" onClick={() => setOpen(!open)}>
      <span className="CertificationsItem-name">{item.name}</span>
      {open && !isReadOnly ? <span onClick={item.delete} style={{ color: '#F86567' }} className="material-icons" aria-hidden="true">highlight_off</span> : null}
      <span className="material-icons" aria-hidden="true">{open ? 'expand_less' : 'expand_more'}</span>
    </div>
  );

  if (!item.id) {
    title = (
      <div className="CertificationsItem-title">
        Add Certifications
        <span style={{ flex: 1 }} />
        <span onClick={item.delete} style={{ color: '#F86567' }} className="material-icons" aria-hidden="true">highlight_off</span>
      </div>
    );
  }

  const searchCertSkillList = async (value) => {
    const data = {
      action: 'GET_SKILLS_CERTIFICATION_LIST',
      field: 'certifications',
      value: value,
      searchId: null
    };
    setSearching(true);
    try {
      const response = await getSkillCertList(data);
      if (response.data && response.data.success) {
        setCertList([...response.data.result]);
      }
    } catch (error) {
      console.error(error);
    }
    setSearching(false);
  };

  const debouncedChangeHandler = useCallback(debounce(searchCertSkillList, 1000), []);

  const handleSearchCertSkill = (value) => {
    if (!value || `${value}`.length < 3) {
      return;
    }
    debouncedChangeHandler(value);
  };

  let form = null;
  const certList = baseCertList.map(cert => (
    <AutoComplete.Option
      key={cert.id}
      value={(cert.id).toString()}
      className="input-box"
    >
      {cert.name}
    </AutoComplete.Option>
  ));

  if (open) {
    form = (
      <div className="CertificationsItem-form">
        <div className="a">
          <Input.AutoComplete
            label="Certification or award"
            dataSource={certList}
            placeholder='Enter min 3 chars to add/search for certifications'
            loading={searching}
            value={data.name}
            onChange={(e, args) => {
              setValue('name', get(args, 'props.children', e));
              if (`${e}`.trim().match(/^[0-9]+$/)) {
                setValue('certification_id', parseInt(e) > 0 ? parseInt(e) : null);
              } else {
                setValue('certification_id', null);
              }
            }}
            optionFilterProp="children"
            onSearch={handleSearchCertSkill}
            filterOption={(input, option) =>
              option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
            className="input-box"
          /></div>
        <div className="b"><Input.Select options={years} label="Year" {...bind('year')} /></div>
      </div>
    );
  }

  return (
    <Step title={title} description={form} {...props} />
  );
};

CertificationsItem.propTypes = {
  item: PropTypes.any,
  cancelAddingNewItem: PropTypes.any,
  service: PropTypes.any
};

export default AdditionalInformationForm;
